/**
 * Gen 2 reasoning observability API helpers.
 *
 * Used by ReasoningTimeline (right pane) and FormulationPanel (third pane)
 * in the therapist conversation view.
 *
 * The api.get helper builds the x-auth-token header literally from the
 * second argument, so we must pass the token explicitly. Falling back to
 * localStorage means callers that haven't been refactored to thread token
 * through props still work.
 */

import api from './api';

function resolveToken(explicit) {
  if (explicit && typeof explicit === 'string') return explicit;
  try {
    return localStorage.getItem('token') || null;
  } catch (_e) {
    return null;
  }
}

/**
 * Resolve the API base segment for the Gen 2 reasoning routes.
 *
 * The same observability components are reused across the therapist view
 * (mounted under /therapist) and the clinical-supervisor view (mounted under
 * /clinical-supervisor). Each role has its own authorization gate, so the
 * caller MUST pass the base matching how they're viewing the client. A
 * supervisor hitting /therapist/gen2/... is not the assignedTherapist and
 * gets a 403, which is exactly the silent-failure bug this guards against.
 */
function resolveApiBase(apiBase) {
  return apiBase === 'clinical-supervisor' ? 'clinical-supervisor' : 'therapist';
}

/**
 * Fetch the ordered "notable" reasoning events for a conversation.
 * Only events the therapist should actually look at — fast-mode no-ops
 * are filtered server-side via the `notable` flag.
 */
export async function fetchReasoningTimeline(conversationId, token, apiBase = 'therapist') {
  const t = resolveToken(token);
  const base = resolveApiBase(apiBase);
  const res = await api.get(`/${base}/gen2/reasoning-timeline/${conversationId}`, t);
  return res.data;
}

/**
 * Fetch the live ClinicalFormulation for a client, plus the diff against
 * the previous version and a list of prior versions.
 */
export async function fetchActiveFormulation(clientId, token, apiBase = 'therapist') {
  const t = resolveToken(token);
  const base = resolveApiBase(apiBase);
  const res = await api.get(`/${base}/gen2/active-formulation/${clientId}`, t);
  return res.data;
}
